import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useToast } from "../../contexts/ToastContext";
import { avatarColor, initials } from "./avatarUtils";

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "Z");
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return "Yesterday";
  const diffDays = (now - d) / 86400000;
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: "short" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function Sidebar({ conversations, activePhone, onSelect, onNewChat, onLogout }) {
  const showToast = useToast();
  const [search, setSearch] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [newPhone, setNewPhone] = useState("");

  const filtered = conversations.filter((c) => {
    const q = search.toLowerCase();
    return (
      (c.display_name || "").toLowerCase().includes(q) ||
      (c.phone || "").includes(q)
    );
  });

  const handleNewChat = () => {
    const cleaned = newPhone.replace(/[\s\-+()]/g, "");
    if (cleaned.length < 10 || !/^\d+$/.test(cleaned)) {
      showToast("Invalid phone number", "warning");
      return;
    }
    setShowNewChat(false);
    setNewPhone("");
    onNewChat(cleaned);
  };

  return (
    <aside className="sidebar" aria-label="Conversations">
      <div className="sidebar-header">
        <div className="sidebar-header-left">
          <Avatar sx={{ bgcolor: "#075e54", width: 40, height: 40 }}>CF</Avatar>
          <h1>CapizFlow</h1>
        </div>
        <div className="sidebar-header-actions">
          <Tooltip title="New chat">
            <IconButton
              size="small"
              onClick={() => setShowNewChat((v) => !v)}
              sx={{ color: "#54656f" }}
              aria-label="New chat"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sign out">
            <IconButton
              size="small"
              onClick={onLogout}
              sx={{ color: "#54656f" }}
              aria-label="Sign out"
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {showNewChat && (
        <div className="new-chat-bar">
          <input
            type="tel"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            placeholder="Enter phone number (e.g. 923001234567)"
            onKeyDown={(e) => e.key === "Enter" && handleNewChat()}
            autoFocus
          />
          <IconButton
            onClick={handleNewChat}
            sx={{
              bgcolor: "#075e54",
              color: "#fff",
              width: 40,
              height: 40,
              "&:hover": { bgcolor: "#054d44" },
            }}
            aria-label="Start chat"
          >
            <SendIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => {
              setShowNewChat(false);
              setNewPhone("");
            }}
            sx={{ color: "#54656f" }}
            aria-label="Cancel"
          >
            <CloseIcon />
          </IconButton>
        </div>
      )}

      <div className="search-box">
        <div className="search-input-wrapper">
          <SearchIcon sx={{ color: "#54656f", fontSize: 18 }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search or start new chat"
          />
        </div>
      </div>

      <div className="conversation-list">
        {filtered.length > 0 ? (
          filtered.map((c) => {
            const name = c.display_name || c.phone;
            const isActive = c.phone === activePhone;
            const isUnread = c.unread_count > 0;
            return (
              <div
                key={c.phone}
                className={`conversation-item ${isActive ? "active" : ""}`}
                onClick={() => onSelect(c.phone, c.display_name || c.phone)}
                role="button"
                tabIndex={0}
              >
                <Avatar
                  sx={{
                    bgcolor: avatarColor(c.phone || name),
                    width: 49,
                    height: 49,
                    fontSize: 18,
                    fontWeight: 500,
                    color: "#fff",
                  }}
                >
                  {initials(name)}
                </Avatar>
                <div className="conversation-info">
                  <div className={`conversation-name ${isUnread ? "unread" : ""}`}>
                    {name}
                  </div>
                  <div className={`conversation-preview ${isUnread ? "unread" : ""}`}>
                    {c.last_direction === "outgoing" && (
                      <DoneAllIcon
                        sx={{
                          fontSize: 16,
                          color: "rgba(17,27,33,0.45)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {(c.last_message || "").substring(0, 60) || "\u00A0"}
                    </span>
                  </div>
                </div>
                <div className="conversation-meta">
                  <div className={`conversation-time ${isUnread ? "unread" : ""}`}>
                    {formatTime(c.last_message_at)}
                  </div>
                  {isUnread && (
                    <Badge
                      badgeContent={c.unread_count}
                      max={999}
                      sx={{
                        "& .MuiBadge-badge": {
                          position: "static",
                          transform: "none",
                          bgcolor: "#25d366",
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 600,
                          height: 20,
                          minWidth: 20,
                          borderRadius: "10px",
                          padding: "0 6px",
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            {search ? "No conversations found" : "No conversations yet"}
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
